import { useState, useEffect, useMemo } from 'react';
import {
  TrendingDown, Play, Pause, RefreshCw, AlertTriangle, CheckCircle, Loader2,
  Layers, DollarSign, Target, Activity, Info,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useActivePolling } from '../hooks/useActivePolling';
import { InfoTip } from '../components/ui';
import {
  previewDcaSafety, deployDcaSafetyBot, listDcaSafetyBots, getDcaSafetyBot,
  getDcaSafetyBotStats, stopDcaSafetyBot, syncDcaSafetyBot, setDcaSafetyAutoRecover,
  getExchangeConnections,
  getExchangeMarkets, getExchangeTicker,
} from '../services/api';
import TradingViewChart from '../components/TradingViewChart';
import DriftBanner from '../components/DriftBanner';

// Tiny placeholder shown for ~50ms before the per-exchange CCXT markets load.
const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT'];

// Same sort as Algo Orders + Grid Bot: popular bases first, stablecoin quote
// priority, alphabetic last. Surfaces the most-traded pairs at the top of
// the full per-exchange list (Coinbase ~900, Binance US ~300, etc.).
function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC', 'DOT', 'UNI', 'ATOM', 'NEAR', 'APT', 'ARB', 'OP'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    const baseRank  = POPULAR_BASES.indexOf(base);
    const quoteRank = POPULAR_QUOTES.indexOf(quote);
    return (quoteRank === -1 ? 100 : quoteRank) * 1000 + (baseRank === -1 ? 999 : baseRank);
  };
  return [...pairs].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export default function DcaSafetyBotPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('create');
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(true);

  const [config, setConfig] = useState({
    exchangeId: '',
    symbol: 'BTC/USDT',
    baseAmount: 100,
    soCount: 5,
    soStepPct: 2,
    soStepScale: 1.2,
    soVolume: 100,
    soVolumeScale: 1.5,
    tpPct: 1.5,
    name: '',
    // Auto-recover (2026-06-08): when ON, the bot automatically starts a new
    // DCA cycle the moment the TP fills, instead of stopping. Matches 3Commas
    // / Pionex "continuous DCA" behavior — set and forget. Default ON because
    // most users running a DCA bot want it to keep working.
    autoRecover: true,
  });
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);

  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(null);
  const [details, setDetails] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getExchangeConnections();
        const list = res?.exchanges?.filter(e => e.connected) || [];
        setConnectedExchanges(list);
        if (list.length && !config.exchangeId) setConfig(c => ({ ...c, exchangeId: list[0].exchangeId }));
      } catch (err) { console.error(err); }
      finally { setLoadingExchanges(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadBots() {
    try {
      const res = await listDcaSafetyBots();
      setBots(res?.data || []);
    } catch (err) { console.error(err); }
  }
  useEffect(() => { if (activeTab === 'live') loadBots(); }, [activeTab]);

  // Polling: refresh list + selectedBot reference + details + stats every 15s
  // so the detail panel reflects state changes (e.g. status flipping to stopped)
  // without requiring a hard refresh.
  const interval = useActivePolling(15000, activeTab === 'live');
  useEffect(() => {
    if (!interval) return;
    const tick = async () => {
      try {
        const res = await listDcaSafetyBots();
        const list = res?.data || [];
        setBots(list);
        if (selectedBot) {
          const updated = list.find(b => b.botId === selectedBot.botId);
          if (updated) setSelectedBot(updated);
          try {
            const [d, s] = await Promise.all([
              getDcaSafetyBot(selectedBot.botId).catch(() => null),
              getDcaSafetyBotStats(selectedBot.botId).catch(() => null),
            ]);
            if (d) setDetails(d?.data);
            if (s) setStats(s?.data);
          } catch {}
        }
      } catch (err) { console.error('DCA live tick failed', err); }
    };
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, selectedBot?.botId]);

  useEffect(() => {
    if (!selectedBot) { setDetails(null); setStats(null); return; }
    (async () => {
      try {
        const [d, s] = await Promise.all([
          getDcaSafetyBot(selectedBot.botId),
          getDcaSafetyBotStats(selectedBot.botId),
        ]);
        setDetails(d?.data);
        setStats(s?.data);
      } catch (err) { console.error(err); }
    })();
  }, [selectedBot]);

  // Auto-preview when config changes (debounced)
  useEffect(() => {
    if (!config.baseAmount || !config.soCount || !config.soStepPct || !config.soVolume || !config.tpPct) {
      setPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        // Backend will fetch live ticker if user has the exchange connected.
        // Otherwise it falls back to a $100 relative anchor (and tells us via priceSource).
        const res = await previewDcaSafety({ ...config });
        setPreview(res?.data);
        setPreviewError(null);
      } catch (err) { setPreview(null); setPreviewError(err.message); }
    }, 350);
    return () => clearTimeout(t);
  // Re-fetch preview when exchange/symbol change too — they affect live ticker
  }, [config.baseAmount, config.soCount, config.soStepPct, config.soStepScale, config.soVolume, config.soVolumeScale, config.tpPct, config.exchangeId, config.symbol]);

  async function handleDeploy() {
    setDeployError(null);
    setDeploying(true);
    try {
      await deployDcaSafetyBot(config);
      setActiveTab('live');
      await loadBots();
    } catch (err) { setDeployError(err.message); }
    finally { setDeploying(false); }
  }

  async function handleStop(botId) {
    if (!confirm('Stop this DCA bot? Open safety orders + take-profit will be canceled. Position remains until manually closed.')) return;
    setBusy(true);
    try {
      await stopDcaSafetyBot(botId);
      // Refresh list + replace the selectedBot reference so detail panel
      // immediately reflects status='stopped' (no hard refresh needed).
      const res = await listDcaSafetyBots();
      const list = res?.data || [];
      setBots(list);
      if (selectedBot?.botId === botId) {
        const updated = list.find(b => b.botId === botId);
        if (updated) setSelectedBot(updated);
        const [d, s] = await Promise.all([
          getDcaSafetyBot(botId).catch(() => null),
          getDcaSafetyBotStats(botId).catch(() => null),
        ]);
        if (d) setDetails(d?.data);
        if (s) setStats(s?.data);
      }
    } catch (err) { alert('Stop failed: ' + err.message); }
    finally { setBusy(false); }
  }

  async function handleSync(botId) {
    setBusy(true);
    try {
      await syncDcaSafetyBot(botId);
      const [d, s] = await Promise.all([getDcaSafetyBot(botId), getDcaSafetyBotStats(botId)]);
      setDetails(d?.data); setStats(s?.data);
    } catch (err) { alert('Sync failed: ' + err.message); }
    finally { setBusy(false); }
  }

  // Toggle auto-restart-cycle on an existing bot. Backend persists the flag;
  // the engine reads it next time TP fills to decide between restart and complete.
  async function handleToggleAutoRecover(botId, nextEnabled) {
    setBusy(true);
    try {
      await setDcaSafetyAutoRecover(botId, nextEnabled);
      // Snappy local update + refetch for confirmation.
      setDetails(prev => prev ? { ...prev, bot: { ...prev.bot, autoRecover: nextEnabled } } : prev);
      const [d, list] = await Promise.all([getDcaSafetyBot(botId), listDcaSafetyBots()]);
      setDetails(d?.data);
      const fresh = (list?.data || []).find(b => b.botId === botId);
      if (fresh) setSelectedBot(fresh);
      setBots(list?.data || []);
    } catch (err) {
      alert('Could not change auto-restart setting: ' + err.message);
    } finally {
      setBusy(false);
    }
  }

  const canDeploy = config.exchangeId && config.symbol && config.baseAmount > 0 && config.soCount > 0 && config.soStepPct > 0 && config.soVolume > 0 && config.tpPct > 0 && !deploying;

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <TrendingDown className="w-7 h-7 text-violet-400" />
          <h1 className="text-2xl font-bold text-white">DCA Bot with Safety Orders</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Starter+</span>
        </div>
        <p className="text-sm text-gray-400">
          Base order + N progressively-larger safety orders below. Take-profit auto-recomputes on the averaged position. <span className="text-emerald-400">Real CCXT execution. No simulation.</span>
        </p>
      </header>

      <div className="border-b border-dark-700/50 flex gap-1">
        <button onClick={() => setActiveTab('create')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'create' ? 'border-violet-400 text-white' : 'border-transparent text-gray-500 hover:text-white'}`}>
          Create DCA Bot
        </button>
        <button onClick={() => setActiveTab('live')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'live' ? 'border-violet-400 text-white' : 'border-transparent text-gray-500 hover:text-white'}`}>
          Live Bots {bots.length > 0 && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-dark-700 text-gray-400">{bots.length}</span>}
        </button>
      </div>

      {activeTab === 'create' && (
        <>
          <TradingViewChart symbol={config.symbol} exchangeId={config.exchangeId} interval="60" height={380} />
          <CreatePanel
            config={config} setConfig={setConfig} preview={preview} previewError={previewError}
            connectedExchanges={connectedExchanges} loadingExchanges={loadingExchanges}
            deploying={deploying} deployError={deployError} onDeploy={handleDeploy} canDeploy={canDeploy}
          />
        </>
      )}
      {activeTab === 'live' && (
        <LivePanel
          bots={bots} selectedBot={selectedBot} setSelectedBot={setSelectedBot}
          details={details} stats={stats} busy={busy} onStop={handleStop} onSync={handleSync}
          onToggleAutoRecover={handleToggleAutoRecover}
        />
      )}
    </div>
  );
}

// ─── Create Panel ────────────────────────────────────────────────────────────

function CreatePanel({ config, setConfig, preview, previewError, connectedExchanges, loadingExchanges, deploying, deployError, onDeploy, canDeploy }) {
  const set = (k) => (e) => setConfig(c => ({ ...c, [k]: e.target.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }));

  // Dynamic per-exchange pair list (same pattern as Algo Orders + Grid Bot).
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');

  useEffect(() => {
    if (!config.exchangeId) return;
    let cancelled = false;
    setPairsLoading(true);
    setPairSearch('');
    getExchangeMarkets(config.exchangeId)
      .then(res => {
        if (cancelled) return;
        const all = (res?.markets || []).map(m => m.symbol);
        if (all.length === 0) { setPairs(FALLBACK_PAIRS); return; }
        const sorted = sortPairs(all);
        setPairs(sorted);
        if (!sorted.includes(config.symbol)) {
          setConfig(c => ({ ...c, symbol: sorted[0] }));
        }
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.exchangeId]);

  const filteredPairs = pairSearch
    ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase())).slice(0, 100)
    : pairs.slice(0, 100);

  // Live ticker for the selected pair — every 15s. Powers the live-price hint
  // shown next to the Symbol selector and (in future) chip helpers.
  const [tickerPrice, setTickerPrice] = useState(null);
  useEffect(() => {
    if (!config.exchangeId || !config.symbol) return;
    let cancelled = false;
    setTickerPrice(null);
    async function refresh() {
      try {
        const t = await getExchangeTicker(config.exchangeId, config.symbol);
        if (!cancelled && t?.last && t.last > 0) setTickerPrice(t.last);
      } catch {}
    }
    refresh();
    const id = setInterval(refresh, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [config.exchangeId, config.symbol]);

  const quote = (config.symbol || '/').split('/')[1] || '';
  const fmtQuote = (n) => {
    if (!Number.isFinite(n)) return '—';
    if (['USDT', 'USDC', 'USD', 'BUSD', 'DAI'].includes(quote)) return n.toFixed(2);
    return n.toFixed(4);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Exchange</label>
          {loadingExchanges ? (
            <div className="bg-dark-700/50 rounded-lg p-3 text-gray-500 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : connectedExchanges.length === 0 ? (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-amber-400 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">No exchange connected.</p>
                <p className="text-amber-300 mt-0.5">Add one in <a href="/settings?tab=connections" className="underline">Settings → Connections</a>.</p>
              </div>
            </div>
          ) : (
            <select value={config.exchangeId} onChange={set('exchangeId')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm">
              {connectedExchanges.map(c => <option key={c.exchangeId} value={c.exchangeId}>{c.name || c.exchangeId}</option>)}
            </select>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-gray-400">Pair</label>
            <span className="text-[10px] text-gray-500">
              {pairsLoading ? 'Loading…' : `${pairs.length.toLocaleString()} on this exchange`}
              {tickerPrice && <span className="ml-2">· Market: <span className="text-cyan-400 font-mono">{fmtQuote(tickerPrice)} {quote}</span></span>}
            </span>
          </div>
          <input
            type="text"
            value={pairSearch}
            onChange={(e) => setPairSearch(e.target.value)}
            placeholder="Search pair (e.g. BTC, ETH/USDC)"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono placeholder-gray-600 mb-2"
          />
          <div className="max-h-40 overflow-y-auto bg-dark-700/50 border border-dark-600 rounded-lg">
            {filteredPairs.length === 0 ? (
              <div className="p-3 text-xs text-gray-500">No pairs match "{pairSearch}"</div>
            ) : (
              filteredPairs.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setConfig(c => ({ ...c, symbol: p }))}
                  className={`block w-full text-left px-3 py-1.5 text-sm font-mono transition-colors ${
                    config.symbol === p
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'text-gray-300 hover:bg-dark-600'
                  }`}
                >
                  {p}
                </button>
              ))
            )}
          </div>
          {pairs.length > 100 && !pairSearch && (
            <p className="text-[10px] text-gray-500 mt-1">Showing first 100 — refine search to find more</p>
          )}
          <div className="mt-2 text-xs text-gray-500">
            Selected: <span className="font-mono text-white">{config.symbol || '(none)'}</span>
          </div>
        </div>

        {/* Preset templates — one-click starting points so users don't have to
            invent 7 numbers from scratch. Custom mode is still the default if
            they want to manually tweak. */}
        <div>
          <label className="text-xs text-gray-400 mb-2 flex items-center gap-1.5">
            Preset (one-click)
            <InfoTip side="right">One-click fills all 7 parameters as a complete ladder. 🟢 Beginner = $15 max exposure / very safe. 🟡 Balanced = $50 / 1.5× averaging. 🔴 Aggressive = $155 / 2× Martingale (risky — avoid for first deploy).</InfoTip>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(() => {
              const PRESETS = [
                { id: 'beginner',   label: '🟢 Beginner',   sub: '$15 total · safe',     cfg: { baseAmount: 5,  tpPct: 1,   soCount: 2, soVolume: 5,  soStepPct: 3, soStepScale: 1,   soVolumeScale: 1 } },
                { id: 'balanced',   label: '🟡 Balanced',   sub: '$50 · 1.5× DCA',       cfg: { baseAmount: 10, tpPct: 1.5, soCount: 4, soVolume: 5,  soStepPct: 2, soStepScale: 1.2, soVolumeScale: 1.5 } },
                { id: 'aggressive', label: '🔴 Aggressive', sub: '$155 · 2× Martingale', cfg: { baseAmount: 10, tpPct: 2,   soCount: 3, soVolume: 5,  soStepPct: 3, soStepScale: 1.3, soVolumeScale: 2 } },
              ];
              const matchesAny = PRESETS.some(p => Object.entries(p.cfg).every(([k, v]) => Number(config[k]) === v));
              // Click Custom → reset to a sensible blank slate so the user can
              // build their own ladder from a clean starting point.
              const customCfg = { baseAmount: 100, tpPct: 1.5, soCount: 5, soVolume: 100, soStepPct: 2, soStepScale: 1.2, soVolumeScale: 1.5 };
              return (
                <>
                  {PRESETS.map(p => {
                    const isActive = Object.entries(p.cfg).every(([k, v]) => Number(config[k]) === v);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setConfig(c => ({ ...c, ...p.cfg }))}
                        className={`text-left rounded-lg p-2.5 border transition-all ${
                          isActive
                            ? 'bg-violet-500/15 border-violet-500/40'
                            : 'bg-dark-700/40 border-dark-600/40 hover:border-dark-500'
                        }`}
                      >
                        <div className="text-xs font-semibold text-white">{p.label}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{p.sub}</div>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setConfig(c => ({ ...c, ...customCfg }))}
                    className={`text-left rounded-lg p-2.5 border transition-all ${
                      !matchesAny
                        ? 'bg-violet-500/15 border-violet-500/40'
                        : 'bg-dark-700/40 border-dark-600/40 hover:border-dark-500'
                    }`}
                    title="Reset to blank defaults — build your own ladder"
                  >
                    <div className="text-xs font-semibold text-white">⚙️ Custom</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {!matchesAny ? 'editing manually' : 'reset to defaults'}
                    </div>
                  </button>
                </>
              );
            })()}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Base order ($)</label>
            <div className="flex gap-1 mb-1 flex-wrap">
              {[5, 10, 25, 50, 100].map(v => (
                <button key={v} type="button"
                  onClick={() => setConfig(c => ({ ...c, baseAmount: v }))}
                  className={`px-2 py-0.5 text-[10px] rounded border ${
                    Number(config.baseAmount) === v
                      ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                      : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                  }`}>${v}</button>
              ))}
            </div>
            <input type="number" step="any" value={config.baseAmount} onChange={set('baseAmount')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            <p className="text-[10px] text-gray-500 mt-1">Initial market buy in quote currency</p>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
              Take-profit %
              <InfoTip side="top">Profit target on the AVERAGED entry, recomputed each time a safety order fills. Tight TP (0.5-1%) = exits often, smaller per-cycle gains. Wide TP (2-3%) = bigger gains but lower fill rate.</InfoTip>
            </label>
            <div className="flex gap-1 mb-1 flex-wrap">
              {[0.5, 1, 1.5, 2, 3].map(v => (
                <button key={v} type="button"
                  onClick={() => setConfig(c => ({ ...c, tpPct: v }))}
                  className={`px-2 py-0.5 text-[10px] rounded border ${
                    Number(config.tpPct) === v
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                      : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                  }`}>{v}%</button>
              ))}
            </div>
            <input type="number" step="0.1" value={config.tpPct} onChange={set('tpPct')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            <p className="text-[10px] text-gray-500 mt-1">Profit on averaged entry</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
              Safety orders count
              <InfoTip side="top">How many limit orders sit BELOW entry waiting for price drops. More layers = more averaging chances but more capital reserved. 3-5 is sweet spot for first deployments. Max 20.</InfoTip>
            </label>
            <div className="flex gap-1 mb-1 flex-wrap">
              {[1, 2, 3, 4, 5].map(v => (
                <button key={v} type="button"
                  onClick={() => setConfig(c => ({ ...c, soCount: v }))}
                  className={`px-2 py-0.5 text-[10px] rounded border ${
                    Number(config.soCount) === v
                      ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                      : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                  }`}>{v}</button>
              ))}
            </div>
            <input type="number" min="1" max="20" value={config.soCount} onChange={set('soCount')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            <p className="text-[10px] text-gray-500 mt-1">1–20 layers below entry</p>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Initial SO size ($)</label>
            <div className="flex gap-1 mb-1 flex-wrap">
              {[5, 10, 25, 50, 100].map(v => (
                <button key={v} type="button"
                  onClick={() => setConfig(c => ({ ...c, soVolume: v }))}
                  className={`px-2 py-0.5 text-[10px] rounded border ${
                    Number(config.soVolume) === v
                      ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                      : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                  }`}>${v}</button>
              ))}
            </div>
            <input type="number" step="any" value={config.soVolume} onChange={set('soVolume')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            <p className="text-[10px] text-gray-500 mt-1">First safety order size</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
              First SO step %
              <InfoTip side="top">How far BELOW entry the FIRST safety order sits. 2-3% works for BTC/ETH; 5-10% for volatile alts where small drops are noise. Combined with Step Scale to determine the deepest SO.</InfoTip>
            </label>
            <div className="flex gap-1 mb-1 flex-wrap">
              {[1, 2, 3, 5, 10].map(v => (
                <button key={v} type="button"
                  onClick={() => setConfig(c => ({ ...c, soStepPct: v }))}
                  className={`px-2 py-0.5 text-[10px] rounded border ${
                    Number(config.soStepPct) === v
                      ? 'bg-red-500/15 border-red-500/40 text-red-300'
                      : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                  }`}>-{v}%</button>
              ))}
            </div>
            <input type="number" step="0.1" value={config.soStepPct} onChange={set('soStepPct')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            <p className="text-[10px] text-gray-500 mt-1">% below entry for SO 1</p>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
              Step scale (×)
              <InfoTip side="top">Controls HOW DEEP the ladder reaches. 1.0 = equal spacing. 1.2 = each step is 20% deeper than the last. 1.5 = aggressive deepening (covers bigger drops but spreads SOs further apart).</InfoTip>
            </label>
            <div className="flex gap-1 mb-1 flex-wrap">
              {[1, 1.2, 1.5, 2].map(v => (
                <button key={v} type="button"
                  onClick={() => setConfig(c => ({ ...c, soStepScale: v }))}
                  className={`px-2 py-0.5 text-[10px] rounded border ${
                    Number(config.soStepScale) === v
                      ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                      : 'bg-dark-900 border-dark-700 text-gray-400 hover:border-dark-600'
                  }`}>{v}×</button>
              ))}
            </div>
            <input type="number" step="0.1" min="1" max="5" value={config.soStepScale} onChange={set('soStepScale')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            <p className="text-[10px] text-gray-500 mt-1">Each step × this (1.0 = even)</p>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 flex items-center gap-2">
            Volume scale (×) — Martingale multiplier
            <InfoTip side="right">Controls HOW MUCH MORE CAPITAL each subsequent SO commits. 1.0 = equal sizes (linear). 1.5 = each SO is 1.5× the prior (moderate averaging). 2.0 = full Martingale (each SO 2× prior — EXPLOSIVE capital exposure at deepest layers).</InfoTip>
            {Number(config.soVolumeScale) >= 2 && (
              <span className="text-[10px] text-red-300 bg-red-500/15 border border-red-500/30 px-1.5 py-0.5 rounded">⚠ aggressive</span>
            )}
          </label>
          <div className="flex gap-1 mb-1 flex-wrap">
            {[
              { v: 1,   label: '1× Even',     accent: 'emerald' },
              { v: 1.5, label: '1.5×',        accent: 'amber'   },
              { v: 2,   label: '2× Doubles',  accent: 'red'     },
              { v: 3,   label: '3× Aggressive', accent: 'red'   },
            ].map(opt => {
              const isOn = Number(config.soVolumeScale) === opt.v;
              const accents = {
                emerald: isOn ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-dark-900 border-dark-700 text-gray-400',
                amber:   isOn ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'       : 'bg-dark-900 border-dark-700 text-gray-400',
                red:     isOn ? 'bg-red-500/15 border-red-500/40 text-red-300'             : 'bg-dark-900 border-dark-700 text-gray-400',
              };
              return (
                <button key={opt.v} type="button"
                  onClick={() => setConfig(c => ({ ...c, soVolumeScale: opt.v }))}
                  className={`px-2 py-0.5 text-[10px] rounded border ${accents[opt.accent]}`}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          <input type="number" step="0.1" min="1" max="5" value={config.soVolumeScale} onChange={set('soVolumeScale')}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
          <p className="text-[10px] text-gray-500 mt-1">Each SO × this size (1.0 = same; 2.0 = doubles each SO)</p>
          {Number(config.soVolumeScale) >= 2 && (
            <div className="mt-2 bg-red-500/5 border border-red-500/20 rounded-lg p-2 text-[11px] text-red-300/90">
              <strong className="text-red-300">Martingale risk:</strong> at scale {config.soVolumeScale}×, each SO is much larger than the prior. After {config.soCount} SOs you'll have committed <strong>${(() => {
                let total = Number(config.baseAmount) || 0;
                let so = Number(config.soVolume) || 0;
                for (let i = 0; i < (Number(config.soCount) || 0); i++) { total += so; so *= Number(config.soVolumeScale); }
                return total.toFixed(2);
              })()}</strong> if all fill. Doubling-down strategies can wipe an account in a sustained downtrend.
            </div>
          )}
        </div>

        {/* Auto-restart cycle toggle — shipped 2026-06-08. When ON, the bot
            automatically starts a new DCA cycle the moment its take-profit
            fills, instead of stopping. Matches 3Commas / Pionex behavior.
            Anti-thrash on backend prevents restart within 5 min of last cycle. */}
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!config.autoRecover}
              onChange={(e) => setConfig(c => ({ ...c, autoRecover: e.target.checked }))}
              className="mt-0.5 accent-emerald-500"
            />
            <div className="flex-1">
              <p className="text-white text-sm font-semibold">Auto-restart cycle when take-profit fills</p>
              <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                The bot will keep cycling indefinitely — buy → safety orders → TP → restart. Set and forget. Turn off if you want a single cycle and then stop.
              </p>
              <p className="text-gray-500 text-[10px] mt-1.5">
                Cooldown: at most one restart per 5 minutes. If a restart fails (e.g. insufficient balance), the bot marks completed and surfaces the error.
              </p>
            </div>
          </label>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Bot name (optional)</label>
          <input value={config.name} onChange={set('name')} placeholder={`DCA ${config.symbol}`}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm" />
        </div>

        {deployError && <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs">{deployError}</div>}

        <button onClick={onDeploy} disabled={!canDeploy}
          className="w-full bg-violet-500 hover:bg-violet-400 disabled:bg-dark-600 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
          {deploying ? <><Loader2 className="w-4 h-4 animate-spin" /> Deploying…</> : <><Play className="w-4 h-4" /> Deploy DCA Bot (real orders)</>}
        </button>
      </div>

      <div className="space-y-4">
        <PreviewCard preview={preview} previewError={previewError} config={config} />
        <RiskCallout />
      </div>
    </div>
  );
}

function PreviewCard({ preview, previewError, config }) {
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5">
      <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
        <Layers className="w-4 h-4 text-violet-400" /> Safety Order Ladder Preview
      </h3>
      {previewError ? (
        <p className="text-red-400 text-sm">{previewError}</p>
      ) : !preview ? (
        <p className="text-gray-500 text-sm">Enter config to see ladder preview.</p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Total capital required" value={`$${formatNum(preview.requiredCapital, 0)}`} accent="amber" />
            <Stat label="Max deviation" value={`${formatNum(preview.maxDeviationPct, 2)}%`} />
            <Stat label="Profit at full deployment" value={`$${formatNum(preview.tpProfitOnFullDeployment, 2)}`} accent="emerald" />
            <Stat label="Safety orders" value={config.soCount} />
          </div>

          <div className="border-t border-dark-700/50 pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Ladder (anchored at base price)</p>
              {preview.priceSource === 'live' && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Live ticker</span>
              )}
              {preview.priceSource === 'fallback' && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30" title="Connect an exchange to see real prices">Relative ($100 anchor)</span>
              )}
            </div>
            <div className="space-y-1 max-h-56 overflow-y-auto">
              <div className="flex items-center justify-between text-xs bg-emerald-500/5 border border-emerald-500/20 rounded p-1.5">
                <span className="text-emerald-400 font-bold">BASE</span>
                <span className="text-gray-400">${formatNum(preview.referencePrice, preview.referencePrice >= 1000 ? 0 : preview.referencePrice >= 1 ? 4 : 6)} (entry)</span>
                <span className="text-white font-mono">${formatNum(config.baseAmount, 0)}</span>
              </div>
              {preview.ladder.map(so => (
                <div key={so.index} className="flex items-center justify-between text-xs bg-dark-700/30 rounded p-1.5">
                  <span className="text-violet-400">SO {so.index}</span>
                  <span className="text-gray-400">${formatNum(so.price, so.price >= 1000 ? 0 : so.price >= 1 ? 4 : 6)} ({formatNum(so.deviationPct, 2)}% ↓)</span>
                  <span className="text-white font-mono">${formatNum(so.amount, 0)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RiskCallout() {
  return (
    <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5">
      <h3 className="text-sm font-bold text-amber-400 mb-2 flex items-center gap-2">
        <Info className="w-4 h-4" /> How DCA safety orders work
      </h3>
      <ul className="space-y-2 text-xs text-gray-300">
        <li>✓ <span className="text-white">Base order</span> opens at current price (market buy).</li>
        <li>✓ <span className="text-white">Safety orders</span> (limit buys) sit below — fill if price drops.</li>
        <li>✓ Each SO fill <span className="text-emerald-400">lowers your average entry</span> and TP target.</li>
        <li>✓ When TP hits, the entire averaged position closes for the configured profit %.</li>
        <li>⚠ <span className="text-amber-400 font-medium">Capital at risk:</span> if all SOs fill, you've used your full capital. If price keeps falling, position is underwater.</li>
        <li>⚠ <span className="text-amber-400 font-medium">Martingale risk:</span> aggressive volume scale (≥2x) magnifies drawdowns. Start with 1.2-1.5x.</li>
      </ul>
    </div>
  );
}

// ─── Live Panel ──────────────────────────────────────────────────────────────

function LivePanel({ bots, selectedBot, setSelectedBot, details, stats, busy, onStop, onSync, onToggleAutoRecover }) {
  // Active / Stopped / All filter — same pattern as Algo Orders + Grid Bot.
  const [statusFilter, setStatusFilter] = useState('active');

  const counts = useMemo(() => ({
    active: bots.filter(b => b.status === 'running').length,
    stopped: bots.filter(b => b.status !== 'running').length,
    all: bots.length,
  }), [bots]);

  const filteredBots = useMemo(() => {
    if (statusFilter === 'active') return bots.filter(b => b.status === 'running');
    if (statusFilter === 'stopped') return bots.filter(b => b.status !== 'running');
    return bots;
  }, [bots, statusFilter]);

  if (bots.length === 0) {
    return (
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
        <TrendingDown className="w-10 h-10 text-gray-600 mx-auto mb-3" />
        <p className="text-white font-medium mb-1">No DCA bots yet</p>
        <p className="text-sm text-gray-500">Switch to <span className="text-violet-400">Create DCA Bot</span> to deploy your first one.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 space-y-2">
        {/* Filter tabs */}
        <div className="inline-flex bg-dark-800 rounded-lg p-0.5 border border-dark-700/50 mb-2 text-xs">
          {[
            { id: 'active',  label: 'Active',  count: counts.active },
            { id: 'stopped', label: 'Stopped', count: counts.stopped },
            { id: 'all',     label: 'All',     count: counts.all },
          ].map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setStatusFilter(t.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                statusFilter === t.id
                  ? 'bg-violet-500/15 text-violet-300'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {t.label}
              <span className={`text-[10px] font-mono px-1 py-0.5 rounded ${statusFilter === t.id ? 'bg-violet-500/20' : 'bg-dark-700'}`}>{t.count}</span>
            </button>
          ))}
        </div>
        {filteredBots.length === 0 ? (
          <div className="bg-dark-800/50 rounded-xl border border-dark-700/50 p-6 text-center">
            <p className="text-sm text-gray-500">No {statusFilter === 'active' ? 'active' : statusFilter === 'stopped' ? 'stopped' : ''} bots.</p>
          </div>
        ) : filteredBots.map(b => {
          const active = selectedBot?.botId === b.botId;
          return (
            <button key={b.botId} onClick={() => setSelectedBot(b)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${active ? 'bg-violet-500/10 border-violet-400/40' : 'bg-dark-800/50 border-dark-700/50 hover:border-dark-600'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-white">{b.name}</span>
                <StatusBadge status={b.status} />
              </div>
              <p className="text-xs text-gray-500 font-mono">{b.exchangeId} · {b.symbol}</p>
              <p className="text-[10px] text-gray-600 mt-1">${b.baseAmount} base · {b.soCount} SOs · {b.tpPct}% TP</p>
            </button>
          );
        })}
      </div>
      <div className="lg:col-span-2">
        {selectedBot && details ? (
          <BotDetails bot={selectedBot} details={details} stats={stats} busy={busy} onStop={onStop} onSync={onSync} onToggleAutoRecover={onToggleAutoRecover} />
        ) : (
          <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
            <p className="text-sm text-gray-500">Select a bot to see SO ladder + TP status.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    running: { label: 'Running', class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    completed: { label: 'Completed', class: 'bg-violet-500/15 text-violet-400 border-violet-500/30' },
    stopped: { label: 'Stopped', class: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  };
  const s = map[status] || { label: status, class: 'bg-dark-700 text-gray-400' };
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.class}`}>{s.label}</span>;
}

function BotDetails({ bot, details, stats, busy, onStop, onSync, onToggleAutoRecover }) {
  const safetyOrders = details?.state?.safetyOrders || [];
  const tpOrder = details?.state?.tpOrder;
  const autoRecover = !!(details?.bot?.autoRecover ?? bot.autoRecover);
  const cycleCount = details?.bot?.cycleCount ?? bot.cycleCount ?? 1;
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-white flex items-center gap-2 flex-wrap">
            {bot.name}
            {cycleCount > 1 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30 uppercase tracking-wider" title={`This bot has completed ${cycleCount - 1} previous cycles and is now on cycle ${cycleCount}.`}>
                Cycle {cycleCount}
              </span>
            )}
            {autoRecover && bot.status === 'running' && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 uppercase tracking-wider" title="Auto-restart is ON — the bot will start a new cycle when this TP fills">
                Auto-restart
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500 font-mono mt-0.5">{bot.exchangeId} · {bot.symbol} · {bot.soCount} SOs · {bot.tpPct}% TP</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onSync(bot.botId)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-dark-600 text-gray-300 hover:border-dark-500 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Sync
          </button>
          {bot.status === 'running' && (
            <button onClick={() => onStop(bot.botId)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 disabled:opacity-50">
              <Pause className="w-3 h-3" /> Stop
            </button>
          )}
        </div>
      </div>

      {/* Auto-restart toggle — exists on running bots only since stopped /
          completed bots have nothing to auto-restart. */}
      {bot.status === 'running' && onToggleAutoRecover && (
        <div className={`rounded-lg p-3 border ${autoRecover ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRecover}
              disabled={busy}
              onChange={(e) => onToggleAutoRecover(bot.botId, e.target.checked)}
              className="mt-0.5 accent-emerald-500"
            />
            <div className="flex-1">
              <p className="text-white text-xs font-semibold">
                Auto-restart cycle when take-profit fills
                {autoRecover ? (
                  <span className="ml-2 text-emerald-400 text-[10px] uppercase tracking-wider">ON</span>
                ) : (
                  <span className="ml-2 text-gray-500 text-[10px] uppercase tracking-wider">OFF</span>
                )}
              </p>
              <p className="text-gray-500 text-[11px] mt-0.5 leading-relaxed">
                {autoRecover
                  ? 'When TP fills, this bot will buy fresh + arm new SOs and TP within ~2 min. Rate-limited to one restart per 5 min.'
                  : 'When TP fills, this bot will stop. Toggle on for continuous cycling like 3Commas / Pionex.'}
              </p>
            </div>
          </label>
        </div>
      )}

      {stats && (
        <>
          {stats.drift && (
            <DriftBanner
              drift={stats.drift}
              asset={(bot?.symbol || '').split('/')[0] || 'asset'}
              onReconciled={() => onSync(bot.botId)}
            />
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Avg entry" value={`$${formatNum(stats.averagedEntry, 4)}`} />
            <Stat label="TP @" value={stats.tpPrice ? `$${formatNum(stats.tpPrice, 4)}` : '—'} />
            <Stat label="SOs filled" value={`${stats.filledSOs} / ${stats.filledSOs + stats.openSOs}`} />
            <Stat label="Realized P&L" value={`$${formatNum(stats.realizedPnl, 2)}`} accent={stats.realizedPnl > 0 ? 'emerald' : stats.realizedPnl < 0 ? 'red' : undefined} />
          </div>

          {/* Open exposure — for DCA, the entire accumulated position is at
              risk until the TP fires. Show cost basis, market value, and
              true total P&L including unrealized. */}
          {stats.openExposure && (
            <div className="rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/5 to-dark-800/40 p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-amber-300 uppercase tracking-wider">Unrealized open exposure</p>
                  <p className="text-[11px] text-amber-200/70 mt-0.5">
                    A DCA bot accumulates a position while price drops, then sells it all at the TP. Until the TP fires, the entire accumulated lot is real capital exposed to further drawdown. The "Realized P&L" above only counts completed cycles.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat
                  label="Open qty"
                  value={formatNum(stats.openExposure.openQty, 6)}
                  subtext={`avg $${formatNum(stats.openExposure.avgEntryUsd, 2)}`}
                />
                <Stat
                  label="Cost basis"
                  value={`$${formatNum(stats.openExposure.openCostUsd, 2)}`}
                  subtext={`${stats.openExposure.filledSOs} SO${stats.openExposure.filledSOs === 1 ? '' : 's'} filled`}
                />
                <Stat
                  label="Market value"
                  value={stats.openExposure.openMarketValueUsd != null ? `$${formatNum(stats.openExposure.openMarketValueUsd, 2)}` : '—'}
                  subtext={stats.openExposure.currentPriceUsd != null ? `at $${formatNum(stats.openExposure.currentPriceUsd, 2)}` : 'price unavailable'}
                />
                <Stat
                  label="Unrealized P&L"
                  value={stats.openExposure.unrealizedPnl != null
                    ? `${stats.openExposure.unrealizedPnl >= 0 ? '+' : ''}$${formatNum(stats.openExposure.unrealizedPnl, 2)}`
                    : '—'}
                  accent={stats.openExposure.unrealizedPnl != null
                    ? (stats.openExposure.unrealizedPnl >= 0 ? 'emerald' : 'red')
                    : undefined}
                  subtext={stats.openExposure.unrealizedPnlPct != null
                    ? `${stats.openExposure.unrealizedPnlPct >= 0 ? '+' : ''}${formatNum(stats.openExposure.unrealizedPnlPct, 2)}%`
                    : undefined}
                />
              </div>

              {stats.openExposure.tpDistancePct != null && (
                <div className="mt-3 text-[11px] text-amber-200/70 bg-dark-900/40 rounded p-2 font-mono">
                  TP @ ${formatNum(stats.openExposure.tpPriceUsd, 2)} · {stats.openExposure.tpDistancePct >= 0
                    ? <>price needs to rise <span className="text-amber-300 font-bold">+{formatNum(stats.openExposure.tpDistancePct, 2)}%</span> to fire TP</>
                    : <>price is <span className="text-emerald-300 font-bold">{formatNum(Math.abs(stats.openExposure.tpDistancePct), 2)}% above TP</span> — should fill soon</>}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Warn loudly if the initial TP placement failed on Coinbase. Previously
          this was silent — bot looked healthy but had no exit order. */}
      {stats?.tpInitialError && !stats.tpPrice && (
        <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="text-red-300 font-semibold">Take-Profit order failed to place on the exchange.</p>
            <p className="text-red-300/80 mt-1">Reason: <span className="font-mono">{stats.tpInitialError}</span></p>
            <p className="text-amber-200/70 mt-2">
              Your position is open but unprotected on the upside — there is no automatic exit. Either stop this bot and manually place a sell limit on Coinbase, or stop the bot to cancel the safety orders and start over.
            </p>
          </div>
        </div>
      )}

      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Safety Order Ladder</p>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {safetyOrders.map(so => (
            <div key={so.index} className="flex items-center justify-between text-xs bg-dark-700/30 rounded p-2 font-mono">
              <span className={so.status === 'filled' ? 'text-emerald-400 font-bold' : so.status === 'canceled' ? 'text-gray-600' : 'text-violet-400'}>
                SO {so.index}
              </span>
              <span className="text-gray-400">${formatNum(so.price, 4)} ({formatNum(so.deviationPct, 2)}% ↓)</span>
              <span className="text-white">${formatNum(so.amountQuote, 0)}</span>
              <span className={`text-[10px] uppercase ${so.status === 'filled' ? 'text-emerald-400' : so.status === 'canceled' ? 'text-gray-600' : 'text-cyan-400'}`}>
                {so.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {tpOrder && (
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Take Profit</p>
          <div className="flex items-center justify-between text-xs bg-emerald-500/5 border border-emerald-500/20 rounded p-2 font-mono">
            <span className="text-emerald-400 font-bold">TP</span>
            <span className="text-gray-400">${formatNum(tpOrder.price, 4)}</span>
            <span className="text-white">{formatNum(tpOrder.amountBase, 6)}</span>
            <span className={`text-[10px] uppercase ${tpOrder.status === 'filled' ? 'text-emerald-400' : tpOrder.status === 'canceled' ? 'text-gray-600' : 'text-cyan-400'}`}>
              {tpOrder.status}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, subtext }) {
  const colorMap = { emerald: 'text-emerald-400', red: 'text-red-400', amber: 'text-amber-400' };
  return (
    <div className="bg-dark-700/30 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-bold font-mono mt-0.5 ${colorMap[accent] || 'text-white'}`}>{value}</p>
      {subtext && <p className="text-[9px] text-gray-500 mt-0.5 font-mono">{subtext}</p>}
    </div>
  );
}
